/**
 * Reverse vesting lets a Company Incorporation / Share Issuance row carry a repurchase schedule
 * over shares that are nonetheless *fully issued* from the moment the event is saved. The schedule
 * has no cap-table effect; it only becomes meaningful at termination.
 *
 * The values deliberately mirror the backend `VestingOnOption` so a stored settings object stays
 * consumable by the existing vesting calculation engine. Only `ISSUANCE_DATE` differs, because
 * "Grant Date" is meaningless on an issuance row.
 */
export enum ReverseVestingOnOption {
  FIRST_DATE = 'first-date',
  ISSUANCE_DATE = 'issuance-date',
  LAST_DAY = 'last-day',
}

export interface ReverseVestingSettings {
  cliffMonths?: number;
  durationMonths: number;
  frequencyMonths: number;
  /** How many of the row's issued shares are subject to repurchase. Never exceeds the row's shares. */
  sharesCount: number;
  /**
   * Anchor for the schedule, the same role Grant Date plays for a plan.
   *
   * Typed as a string, not a Date: this object is persisted in a `jsonb` column, and Postgres
   * returns jsonb dates as strings. Declaring `Date` here would be a lie that survives typecheck
   * and then throws on the first date method after a read.
   */
  startDate: string;
  vestingOn: ReverseVestingOnOption;
}

/**
 * What a termination actually settled on one reverse-vested row, frozen at the moment the
 * stakeholder was terminated.
 *
 * Persisted rather than recomputed on read. The contractual position is evaluated as of the
 * stakeholder-level Last Vesting Date, which is not the termination date and is not derivable from
 * the stakeholder afterwards — and `acceleratedShares` and `repurchasePrice` are operator input
 * with no formula behind them at all. Recomputing would silently produce a different repurchase
 * than the one the company paid for.
 *
 * Dates are strings for the same reason as `ReverseVestingSettings.startDate`: this object lives in
 * a `jsonb` column, and Postgres hands jsonb dates back as strings.
 */
export interface ReverseVestingTerminationSettlement {
  /** Unvested shares pulled forward to vest immediately on termination. Zero unless overridden. */
  acceleratedShares: number;
  /** `repurchasePrice` × `lapsedShares` — what the company pays to take the lapsed shares back. */
  compensation: number;
  /**
   * Vested portion of the schedule as of `lastVestingDate`, before any acceleration, narrowed to
   * the shares the holder still held.
   */
  contractualVestedShares: number;
  /**
   * Schedule shares the holder no longer held at termination (sold, bought back or converted out of
   * the share class), attributed vested-first — nothing to repurchase and nothing to compensate.
   * Absent on rows settled before disposals were accounted for, which read as zero.
   */
  disposedShares?: number;
  /** `contractualVestedShares` + `acceleratedShares` — what the holder keeps. */
  finalVestedShares: number;
  /**
   * Shares repurchased and cancelled: `reverseVestingShares` − `finalVestedShares` −
   * `disposedShares`.
   */
  lapsedShares: number;
  /** The stakeholder-level Last Vesting Date the contractual position was evaluated at. */
  lastVestingDate: string;
  /** Price per lapsed share. Carries up to 7 decimals, so par values do not round to zero. */
  repurchasePrice: number;
  /**
   * The schedule's share count at settlement time. Copied rather than read back off
   * `ReverseVestingSettings` so the settled arithmetic stays checkable even if the schedule is
   * later edited.
   */
  reverseVestingShares: number;
  terminationDate: string;
}
